import React, { useState, useEffect, useCallback } from 'react';
import {
    CheckCircle,
    AlertTriangle,
    XCircle,
    Clock,
    RefreshCw,
    FileText,
    ChevronDown,
    ChevronUp,
    Loader
} from 'lucide-react';
import './UploadJobsPanel.css';

const StatusBadge = ({ status }) => {
    const statusConfig = {
        pending: { icon: Clock, label: 'Pending', className: 'status-pending' },
        processing: { icon: Loader, label: 'Processing', className: 'status-processing' },
        completed: { icon: CheckCircle, label: 'Completed', className: 'status-completed' },
        partial_failure: { icon: AlertTriangle, label: 'Partial', className: 'status-partial' },
        failed: { icon: XCircle, label: 'Failed', className: 'status-failed' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
        <span className={`status-badge ${config.className}`}>
            <Icon size={14} className={status === 'processing' ? 'spin' : ''} />
            {config.label}
        </span>
    );
};

const PageStatusRow = ({ page }) => {
    return (
        <div className={`page-row ${page.status === 'failed' ? 'page-failed' : 'page-success'}`}>
            <span className="page-number">Page {page.page_number}</span>
            <span className={`page-status ${page.status}`}>
                {page.status === 'success' ? (
                    <CheckCircle size={14} />
                ) : (
                    <XCircle size={14} />
                )}
                {page.status}
            </span>
            {page.error_reason && (
                <span className="page-error">{page.error_reason}</span>
            )}
        </div>
    );
};

const JobRow = ({ job, onViewDetails }) => {
    const [expanded, setExpanded] = useState(false);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);

        // Format in IST (India Standard Time)
        const options = {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        };

        return date.toLocaleString('en-IN', options);
    };

    const toggleExpand = () => setExpanded(!expanded);

    const successPages = job.pages?.filter(p => p.status === 'success').length || 0;
    const totalPages = job.total_pages || job.pages?.length || 0;

    return (
        <>
            <tr className="job-row" onClick={toggleExpand}>
                <td className="job-filename">
                    <FileText size={16} />
                    <span>{job.filename || 'document.pdf'}</span>
                </td>
                <td>
                    <StatusBadge status={job.status} />
                </td>
                <td className="job-pages">
                    {job.status === 'pending' ? (
                        <span className="pages-pending">-/-</span>
                    ) : (
                        <span className={successPages === totalPages ? 'pages-all' : 'pages-partial'}>
                            {successPages}/{totalPages}
                        </span>
                    )}
                </td>
                <td className="job-date">
                    {formatDate(job.created_at)}
                </td>
                <td className="job-actions">
                    <button
                        className="expand-btn"
                        onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
                    >
                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                </td>
            </tr>
            {expanded && job.pages && job.pages.length > 0 && (
                <tr className="job-details-row">
                    <td colSpan={5}>
                        <div className="pages-container">
                            {job.pages.map((page, idx) => (
                                <PageStatusRow key={idx} page={page} />
                            ))}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const UploadJobsPanel = ({ orderId }) => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [autoRefresh, setAutoRefresh] = useState(true);

    const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

    const fetchJobs = useCallback(async () => {
        try {
            const url = orderId
                ? `${API_URL}/api/orders/${orderId}/upload-jobs?page=${page}&limit=10`
                : `${API_URL}/api/upload-jobs?page=${page}&limit=10`;

            const response = await fetch(url, {
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch upload jobs');
            }

            const data = await response.json();
            setJobs(data.jobs || []);
            setTotalPages(data.total_pages || 1);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [API_URL, orderId, page]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    // Auto-refresh every 5 seconds if there are pending/processing jobs
    useEffect(() => {
        if (!autoRefresh) return;

        const hasPendingJobs = jobs.some(j => ['pending', 'processing'].includes(j.status));
        if (!hasPendingJobs) return;

        const interval = setInterval(fetchJobs, 5000);
        return () => clearInterval(interval);
    }, [jobs, autoRefresh, fetchJobs]);

    const handleRefresh = () => {
        setLoading(true);
        fetchJobs();
    };

    if (loading && jobs.length === 0) {
        return (
            <div className="upload-jobs-panel loading">
                <Loader className="spin" size={24} />
                <span>Loading upload jobs...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="upload-jobs-panel error">
                <XCircle size={24} />
                <span>{error}</span>
                <button onClick={handleRefresh}>Retry</button>
            </div>
        );
    }

    if (jobs.length === 0) {
        return (
            <div className="upload-jobs-panel empty">
                <FileText size={32} />
                <p>No upload jobs found</p>
                <small>Upload files will appear here with their processing status</small>
            </div>
        );
    }

    return (
        <div className="upload-jobs-panel">
            <div className="panel-header">
                <h3>Upload Jobs</h3>
                <div className="header-actions">
                    <label className="auto-refresh-toggle">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                        />
                        Auto-refresh
                    </label>
                    <button className="refresh-btn" onClick={handleRefresh} disabled={loading}>
                        <RefreshCw size={16} className={loading ? 'spin' : ''} />
                    </button>
                </div>
            </div>

            <table className="jobs-table">
                <thead>
                    <tr>
                        <th>Filename</th>
                        <th>Status</th>
                        <th>Pages</th>
                        <th>Uploaded</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {jobs.map((job) => (
                        <JobRow key={job.id} job={job} />
                    ))}
                </tbody>
            </table>

            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                    >
                        Previous
                    </button>
                    <span>Page {page} of {totalPages}</span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default UploadJobsPanel;
